import { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import {
  buildHitLabLoginPath,
  buildHitLabIntakeReturnTo,
  isHitLabClientEnabled,
} from './hitLabUtils';
import './hitLab.css';

const SOCIAL_LINKS = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/thejoegalaxy',
  },
  {
    label: 'X',
    href: 'https://x.com/joegalaxy1',
  },
];

export default function HitLabLanding() {
  const { player } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const enabled = isHitLabClientEnabled();

  const startIntake = () => {
    if (!enabled) return;
    if (player?.token) {
      history.push(buildHitLabIntakeReturnTo(location.search));
      return;
    }
    history.push(buildHitLabLoginPath(location.search));
  };

  return (
    <main className="hitlab-page hitlab-landing">
      <Helmet>
        <title>JG HitLab Artist Intake | Joe Galaxy</title>
        <meta
          name="description"
          content="Submit one original record for the Joe Galaxy curated artist intake."
        />
      </Helmet>

      <section className="hitlab-hero">
        <div className="hitlab-shell hitlab-hero-grid">
          <div className="hitlab-hero-copy">
            <p className="hitlab-kicker">JG HITLAB ARTIST INTAKE</p>
            <h1>Submit Your Best Song to Joe Galaxy</h1>
            <p className="hitlab-lede">
              A curated intake for independent artists with a real record, clear
              rights, and the confidence to let the song speak.
            </p>
            <div className="hitlab-cta-row">
              <button
                type="button"
                className="hitlab-primary-btn"
                onClick={startIntake}
                disabled={!enabled}
              >
                {enabled
                  ? 'Create Artist Profile + Start Song Intake'
                  : 'Artist intake opens soon'}
              </button>
            </div>
          </div>
          <aside className="hitlab-hero-card" aria-label="JG HitLab signal stack">
            <div className="hitlab-vinyl" aria-hidden="true">
              <span />
            </div>
            <p>Health. Wealth. Love. Party. Rebel.</p>
            <h2>Curated ears. Serious records.</h2>
            <small>
              One song enters the review queue. Quality, rights clarity, and
              fit decide what happens next.
            </small>
          </aside>
        </div>
      </section>

      <section className="hitlab-shell hitlab-proof-grid" aria-label="Joe Galaxy credibility">
        <article className="hitlab-wide-card">
          <span>JOE GALAXY</span>
          <h2>Joe Galaxy is opening a curated artist intake for records with real potential.</h2>
          <p>
            This is built for artists who want a clean path into the JG universe:
            JG HitLab consideration, Hit Radar attention, members-only music
            rotation review, and future LocalOps music-promo opportunities when
            the record earns that next step.
          </p>
        </article>
        <article>
          <span>VISUAL SLOT</span>
          <h3>Artist energy</h3>
          <p>Reserved for approved Joe Galaxy or JG HitLab visuals.</p>
        </article>
        <article>
          <span>VISUAL SLOT</span>
          <h3>Curator signal</h3>
          <p>Reserved for brand photography, artwork, or campaign creative.</p>
        </article>
      </section>

      <section className="hitlab-band">
        <div className="hitlab-shell hitlab-opportunity">
          <div>
            <p className="hitlab-kicker">THE PATH</p>
            <h2>Submit one original record. Keep the rights clean.</h2>
          </div>
          <div className="hitlab-path-list">
            <article>
              <strong>JG HitLab</strong>
              <span>Curated internal review for songs with standout potential.</span>
            </article>
            <article>
              <strong>Hit Radar</strong>
              <span>A possible next signal when a record earns attention.</span>
            </article>
            <article>
              <strong>Members Rotation</strong>
              <span>Possible private member listening consideration.</span>
            </article>
            <article>
              <strong>LocalOps Music Promo</strong>
              <span>Future street-level opportunities when a campaign fits.</span>
            </article>
          </div>
        </div>
      </section>

      <section className="hitlab-shell hitlab-gate">
        <div>
          <p className="hitlab-kicker">QUALITY GATE</p>
          <h2>Send the record you can stand behind.</h2>
          <p>
            The intake is for one original song. Every record must be clear
            enough for private HitLab review.
          </p>
        </div>
        <ul>
          <li>One original song only</li>
          <li>No covers</li>
          <li>No uncleared samples</li>
          <li>No stolen beats</li>
          <li>No unclear producer rights</li>
          <li>No unapproved features</li>
          <li>No placement guaranteed</li>
        </ul>
      </section>

      <section className="hitlab-shell hitlab-final-cta">
        <div>
          <p className="hitlab-kicker">READY</p>
          <h2>Your strongest song gets the cleanest shot.</h2>
        </div>
        <button
          type="button"
          className="hitlab-primary-btn"
          onClick={startIntake}
          disabled={!enabled}
        >
          {enabled
            ? 'Create Artist Profile + Start Song Intake'
            : 'Artist intake opens soon'}
        </button>
        <nav className="hitlab-socials" aria-label="Joe Galaxy social links">
          {SOCIAL_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {link.label}
            </a>
          ))}
        </nav>
      </section>
    </main>
  );
}
